import { useState, useCallback, useMemo, useEffect } from 'react';
import { InventoryFilterState } from '../../../types/inventory';
import type { ListPageActiveFilterChip } from '../../common/list-page';

export interface UseInventoryQueryFiltersProps {
  categories: { id: number; name: string }[];
  locations: { id: number; name: string }[];
  statuses: { id: string; name: string }[];
  tags?: { id: number; name: string }[];
  colors?: { id: number; name: string }[];
  materials?: { id: number; name: string }[];
  warehousePlacementStatuses?: { id: string; name: string }[];
  branchAvailabilityStatuses?: { id: string; name: string }[];
  onFilterChange: (filters: InventoryFilterState) => void;
  initialFilters?: InventoryFilterState;
  selectedItems?: string[];
  onTransferClick?: () => void;
  onRequestClick?: () => void;
  /** Enter en "Escanear código de barras" (Más filtros): selecciona pieza para transferencia. */
  onScanSubmit?: (code: string) => void | Promise<void>;
  isInventoryBranchQuery?: boolean;
  isInventoryWarehouseQuery?: boolean;
  isLocationLocked?: boolean;
  /** Tras cambiar alcance de sucursal (gestión en sucursal). */
  onLocationScopeChange?: (payload: { locationId: string; locationName: string }) => void;
}

const isActiveFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value !== '';
  if (typeof value === 'number') return !Number.isNaN(value);
  return false;
};

export const useInventoryQueryFilters = ({
  categories = [],
  locations = [],
  statuses = [],
  tags = [],
  colors = [],
  materials = [],
  warehousePlacementStatuses = [],
  branchAvailabilityStatuses = [],
  onFilterChange,
  initialFilters,
  selectedItems = [],
  onTransferClick,
  onRequestClick,
  onScanSubmit,
  isInventoryBranchQuery = false,
  isInventoryWarehouseQuery = false,
  isLocationLocked = false,
  onLocationScopeChange,
}: UseInventoryQueryFiltersProps) => {
  // Filtros por defecto - Solo los que realmente funcionan
  const defaultFilters: InventoryFilterState = {
    searchTerm: '',
    createdAtStart: '',
    createdAtEnd: '',
    status: '',
    locationId: '',
    categoryName: '', // Tipo de producto
    recordCode: '', // Número de registro
    searchTag: '', // Etiqueta
    materialId: '', // Material ID
    diameterSize: '', // Talla (API diameterSize)
    color: '', // Color
    workshopCode: '', // Workshop code
    warehousePlacementStatus: '',
    branchAvailabilityStatus: '',
    weightGramsMin: undefined,
    weightGramsMax: undefined,
    salePriceMin: undefined,
    salePriceMax: undefined,
    salePricePerGramMin: undefined,
    salePricePerGramMax: undefined,
  };

  // Estados
  const [filters, setFilters] = useState<InventoryFilterState>(initialFilters || defaultFilters);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');

  // Sync locationId when parent auto-assigns (locked primary) or updates scope externally
  useEffect(() => {
    const nextLocationId = initialFilters?.locationId;
    if (nextLocationId === undefined) return;
    setFilters((prev) =>
      prev.locationId === nextLocationId ? prev : { ...prev, locationId: nextLocationId },
    );
  }, [initialFilters?.locationId]);
  // Manejar cambios en inputs
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  /** Aplica filtros y dispara búsqueda en la página padre (API). */
  const mergeAndCommit = useCallback(
    (patch: Partial<InventoryFilterState>) => {
      setFilters((prev) => {
        const next = { ...prev, ...patch };
        onFilterChange(next);
        return next;
      });
    },
    [onFilterChange],
  );

  // Manejar cambio de tags (múltiple selección)
  const handleTagsChange = useCallback(
    (event: any, newValue: any) => {
      const tagNames = newValue.map((tag: any) => tag.name).join(',');
      mergeAndCommit({ searchTag: tagNames });
    },
    [mergeAndCommit],
  );

  const handleColorsChange = useCallback(
    (event: any, newValue: any) => {
      const colorNames = newValue.map((c: any) => c.name).join(',');
      mergeAndCommit({ color: colorNames });
    },
    [mergeAndCommit],
  );

  // Manejar cambio de categorías (selección única)
  const handleCategoriesChange = useCallback(
    (event: any, newValue: any) => {
      mergeAndCommit({ categoryName: newValue ? newValue.name : '' });
    },
    [mergeAndCommit],
  );

  // Manejar cambio de estados (selección única)
  const handleStatusesChange = useCallback(
    (event: any, newValue: any) => {
      mergeAndCommit({ status: newValue ? newValue.id : '' });
    },
    [mergeAndCommit],
  );

  // Manejar cambio de ubicaciones (selección única)
  const handleLocationsChange = useCallback(
    (event: any, newValue: any) => {
      if (isLocationLocked) return;
      mergeAndCommit({ locationId: newValue ? newValue.id.toString() : '' });
    },
    [isLocationLocked, mergeAndCommit],
  );

  const handleLocationScopeChange = useCallback(
    (payload: { locationId: string; locationName: string }) => {
      if (isLocationLocked) return;
      mergeAndCommit({ locationId: payload.locationId });
      onLocationScopeChange?.(payload);
    },
    [isLocationLocked, mergeAndCommit, onLocationScopeChange],
  );

  // Manejar cambio de material (selección única)
  const handleMaterialChange = useCallback(
    (event: any, newValue: any) => {
      mergeAndCommit({ materialId: newValue ? newValue.id.toString() : '' });
    },
    [mergeAndCommit],
  );

  const handleWarehousePlacementChange = useCallback(
    (event: any, newValue: any) => {
      mergeAndCommit({ warehousePlacementStatus: newValue ? newValue.id : '' });
    },
    [mergeAndCommit],
  );

  const handleBranchAvailabilityChange = useCallback(
    (event: any, newValue: any) => {
      mergeAndCommit({ branchAvailabilityStatus: newValue ? newValue.id : '' });
    },
    [mergeAndCommit],
  );

  /** Actualiza peso/precio en estado local; la búsqueda se dispara con Enter, Buscar o al soltar el slider. */
  const handlePieceNumericFiltersPatch = useCallback(
    (
      partial: Partial<
        Pick<
          InventoryFilterState,
          | 'weightGramsMin'
          | 'weightGramsMax'
          | 'salePriceMin'
          | 'salePriceMax'
          | 'salePricePerGramMin'
          | 'salePricePerGramMax'
        >
      >,
    ) => {
      setFilters((prev) => ({ ...prev, ...partial }));
    },
    [],
  );

  /** Persiste peso/precio y dispara búsqueda (p. ej. al soltar el slider). */
  const handlePieceNumericFiltersSearch = useCallback(
    (
      partial: Partial<
        Pick<
          InventoryFilterState,
          | 'weightGramsMin'
          | 'weightGramsMax'
          | 'salePriceMin'
          | 'salePriceMax'
          | 'salePricePerGramMin'
          | 'salePricePerGramMax'
        >
      >,
    ) => {
      setFilters((prev) => {
        const next = { ...prev, ...partial };
        onFilterChange(next);
        return next;
      });
    },
    [onFilterChange],
  );

  // Obtener tags seleccionadas para el Autocomplete
  const selectedTags = useMemo(() => {
    if (!filters.searchTag) return [];
    const tagNames = filters.searchTag
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name);
    return tags.filter((tag) => tagNames.includes(tag.name));
  }, [tags, filters.searchTag]);

  const selectedColors = useMemo(() => {
    if (!filters.color) return [];
    const colorNames = filters.color
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name);
    return colors.filter((c) => colorNames.includes(c.name));
  }, [colors, filters.color]);

  // Obtener categoría seleccionada para el Autocomplete
  const selectedCategories = useMemo(() => {
    if (!filters.categoryName) return null;
    return categories.find((category) => category.name === filters.categoryName) || null;
  }, [categories, filters.categoryName]);

  // Obtener estado seleccionado para el Autocomplete
  const selectedStatuses = useMemo(() => {
    if (!filters.status) return null;
    return statuses.find((status) => status.id === filters.status) || null;
  }, [statuses, filters.status]);

  // Obtener ubicación seleccionada para el Autocomplete
  const selectedLocations = useMemo(() => {
    if (!filters.locationId) return null;
    return locations.find((location) => location.id.toString() === filters.locationId) || null;
  }, [locations, filters.locationId]);

  // Obtener material seleccionado para el Autocomplete
  const selectedMaterial = useMemo(() => {
    return materials.find((material) => material.id.toString() === filters.materialId) || null;
  }, [materials, filters.materialId]);

  const selectedWarehousePlacement = useMemo(() => {
    if (!filters.warehousePlacementStatus) return null;
    return (
      warehousePlacementStatuses.find((s) => s.id === filters.warehousePlacementStatus) || null
    );
  }, [warehousePlacementStatuses, filters.warehousePlacementStatus]);

  const selectedBranchAvailability = useMemo(() => {
    if (!filters.branchAvailabilityStatus) return null;
    return (
      branchAvailabilityStatuses.find((s) => s.id === filters.branchAvailabilityStatus) || null
    );
  }, [branchAvailabilityStatuses, filters.branchAvailabilityStatus]);

  // Manejar tecla Enter — usa el estado más reciente (incl. último carácter tipeado)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setFilters((prev) => {
          onFilterChange(prev);
          return prev;
        });
      }
    },
    [onFilterChange],
  );

  const handleBarcodeInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setBarcodeInput(e.target.value);
  }, []);

  const handleBarcodeScanKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing) {
        return;
      }
      e.preventDefault();
      const raw = (e.target as HTMLInputElement).value.trim();
      if (raw && onScanSubmit) {
        void onScanSubmit(raw);
      }
      setBarcodeInput('');
    },
    [onScanSubmit],
  );

  // Resetear filtros (en gestión sucursal/almacén el alcance no se limpia con Reiniciar)
  const handleReset = useCallback(() => {
    const nextFilters =
      isLocationLocked || isInventoryBranchQuery || isInventoryWarehouseQuery
        ? { ...defaultFilters, locationId: filters.locationId }
        : { ...defaultFilters };
    setFilters(nextFilters);
    setBarcodeInput('');
    onFilterChange(nextFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onFilterChange,
    isLocationLocked,
    isInventoryBranchQuery,
    isInventoryWarehouseQuery,
    filters.locationId,
  ]);

  const handleToggleMoreFilters = useCallback(() => {
    setShowMoreFilters((prev) => !prev);
  }, []);

  const handleSearch = useCallback(() => {
    setFilters((prev) => {
      onFilterChange(prev);
      return prev;
    });
  }, [onFilterChange]);

  const handleTransfer = useCallback(() => {
    if (onTransferClick) {
      onTransferClick();
    }
  }, [onTransferClick]);

  const handleRequest = useCallback(() => {
    if (onRequestClick) {
      onRequestClick();
    }
  }, [onRequestClick]);

  const hasActiveFilters = useCallback(() => {
    return Object.entries(filters).some(([key, value]) => {
      if ((isInventoryBranchQuery || isInventoryWarehouseQuery) && key === 'locationId') {
        return false;
      }
      return isActiveFilterValue(value);
    });
  }, [filters, isInventoryBranchQuery, isInventoryWarehouseQuery]);

  // Contar filtros activos (locationId no cuenta en chip "Más" si es alcance de toolbar)
  const activeFiltersCount = useCallback(() => {
    return Object.entries(filters).filter(([key, value]) => {
      if ((isInventoryBranchQuery || isInventoryWarehouseQuery) && key === 'locationId') {
        return false;
      }
      return isActiveFilterValue(value);
    }).length;
  }, [filters, isInventoryBranchQuery, isInventoryWarehouseQuery]);

  // Verificar si hay items seleccionados
  const hasSelectedItems = selectedItems.length > 0;

  // Verificar si se puede transferir
  const canTransfer = hasSelectedItems && onTransferClick;

  const removeCommaValue = useCallback((csv: string | undefined, value: string) => {
    return (csv || '')
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part && part !== value)
      .join(',');
  }, []);

  /** Clear one applied field without committing unrelated draft-only inputs. */
  const clearAppliedPatch = useCallback(
    (patch: Partial<InventoryFilterState>) => {
      const base = initialFilters || defaultFilters;
      const nextApplied = { ...base, ...patch };
      setFilters((prev) => ({ ...prev, ...patch }));
      onFilterChange(nextApplied);
    },
    [initialFilters, onFilterChange],
  );

  /** Chips from applied parent filters (initialFilters), not draft-only text. */
  const activeFilterChips: ListPageActiveFilterChip[] = useMemo(() => {
    const applied = initialFilters;
    if (!applied) return [];

    const chips: ListPageActiveFilterChip[] = [];
    const isScopeLocation = isInventoryBranchQuery || isInventoryWarehouseQuery;

    const push = (key: string, label: string, patch: Partial<InventoryFilterState>) => {
      chips.push({
        key,
        label,
        onRemove: () => clearAppliedPatch(patch),
      });
    };

    if (isActiveFilterValue(applied.searchTerm)) {
      push('searchTerm', `Búsqueda: ${applied.searchTerm}`, { searchTerm: '' });
    }
    if (isActiveFilterValue(applied.categoryName)) {
      push('categoryName', `Tipo: ${applied.categoryName}`, { categoryName: '' });
    }
    if (isActiveFilterValue(applied.recordCode)) {
      push('recordCode', `Registro: ${applied.recordCode}`, { recordCode: '' });
    }
    if (isActiveFilterValue(applied.status)) {
      const statusName = statuses.find((s) => s.id === applied.status)?.name || applied.status;
      push('status', `Estado: ${statusName}`, { status: '' });
    }
    if (isActiveFilterValue(applied.warehousePlacementStatus)) {
      const name =
        warehousePlacementStatuses.find((s) => s.id === applied.warehousePlacementStatus)?.name ||
        applied.warehousePlacementStatus;
      push('warehousePlacementStatus', `Estado almacén: ${name}`, {
        warehousePlacementStatus: '',
      });
    }
    if (isActiveFilterValue(applied.branchAvailabilityStatus)) {
      const name =
        branchAvailabilityStatuses.find((s) => s.id === applied.branchAvailabilityStatus)?.name ||
        applied.branchAvailabilityStatus;
      push('branchAvailabilityStatus', `Estado sucursal: ${name}`, {
        branchAvailabilityStatus: '',
      });
    }
    if (!isScopeLocation && isActiveFilterValue(applied.locationId)) {
      const locName =
        locations.find((l) => l.id.toString() === applied.locationId)?.name || applied.locationId;
      push('locationId', `Ubicación: ${locName}`, { locationId: '' });
    }
    (applied.searchTag || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((tag) => {
        chips.push({
          key: `searchTag:${tag}`,
          label: `Etiqueta: ${tag}`,
          onRemove: () =>
            clearAppliedPatch({ searchTag: removeCommaValue(applied.searchTag, tag) }),
        });
      });
    (applied.color || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)
      .forEach((colorName) => {
        chips.push({
          key: `color:${colorName}`,
          label: `Color: ${colorName}`,
          onRemove: () => clearAppliedPatch({ color: removeCommaValue(applied.color, colorName) }),
        });
      });
    if (isActiveFilterValue(applied.materialId)) {
      const matName =
        materials.find((m) => m.id.toString() === applied.materialId)?.name || applied.materialId;
      push('materialId', `Material: ${matName}`, { materialId: '' });
    }
    if (isActiveFilterValue(applied.diameterSize)) {
      push('diameterSize', `Talla: ${applied.diameterSize}`, { diameterSize: '' });
    }
    if (isActiveFilterValue(applied.workshopCode)) {
      push('workshopCode', `Taller: ${applied.workshopCode}`, { workshopCode: '' });
    }
    if (isActiveFilterValue(applied.createdAtStart)) {
      push('createdAtStart', `Desde: ${applied.createdAtStart}`, { createdAtStart: '' });
    }
    if (isActiveFilterValue(applied.createdAtEnd)) {
      push('createdAtEnd', `Hasta: ${applied.createdAtEnd}`, { createdAtEnd: '' });
    }
    if (isActiveFilterValue(applied.weightGramsMin)) {
      push('weightGramsMin', `Peso min: ${applied.weightGramsMin}g`, {
        weightGramsMin: undefined,
      });
    }
    if (isActiveFilterValue(applied.weightGramsMax)) {
      push('weightGramsMax', `Peso max: ${applied.weightGramsMax}g`, {
        weightGramsMax: undefined,
      });
    }
    if (isActiveFilterValue(applied.salePriceMin)) {
      push('salePriceMin', `Precio min: ${applied.salePriceMin}`, {
        salePriceMin: undefined,
      });
    }
    if (isActiveFilterValue(applied.salePriceMax)) {
      push('salePriceMax', `Precio max: ${applied.salePriceMax}`, {
        salePriceMax: undefined,
      });
    }
    if (isActiveFilterValue(applied.salePricePerGramMin)) {
      push('salePricePerGramMin', `Bs/g min: ${applied.salePricePerGramMin}`, {
        salePricePerGramMin: undefined,
      });
    }
    if (isActiveFilterValue(applied.salePricePerGramMax)) {
      push('salePricePerGramMax', `Bs/g max: ${applied.salePricePerGramMax}`, {
        salePricePerGramMax: undefined,
      });
    }

    return chips;
  }, [
    initialFilters,
    isInventoryBranchQuery,
    isInventoryWarehouseQuery,
    locations,
    statuses,
    materials,
    warehousePlacementStatuses,
    branchAvailabilityStatuses,
    clearAppliedPatch,
    removeCommaValue,
  ]);

  return {
    // Estados
    filters,
    showMoreFilters,
    barcodeInput,
    hasActiveFilters: hasActiveFilters(),
    activeFiltersCount: activeFiltersCount(),
    activeFilterChips,
    hasSelectedItems,
    canTransfer,

    // Handlers
    handleChange,
    handleKeyDown,
    handleBarcodeInputChange,
    handleBarcodeScanKeyDown,
    handleReset,
    handleToggleMoreFilters,
    handleSearch,
    handleTransfer,
    handleRequest,
    mergeAndCommit,
    handleTagsChange,
    handleColorsChange,
    handleCategoriesChange,
    handleStatusesChange,
    handleLocationsChange,
    handleLocationScopeChange,
    handleMaterialChange,
    handleWarehousePlacementChange,
    handleBranchAvailabilityChange,
    handlePieceNumericFiltersPatch,
    handlePieceNumericFiltersSearch,

    // Datos
    categories,
    locations,
    statuses,
    materials,
    warehousePlacementStatuses,
    branchAvailabilityStatuses,
    selectedTags,
    selectedColors,
    selectedCategories,
    selectedStatuses,
    selectedLocations,
    selectedMaterial,
    selectedWarehousePlacement,
    selectedBranchAvailability,
  };
};
